import {
  useAllCategoriesQuery,
  useAllCategoryGroupsQuery,
} from '@transactions/graphql';
import {ArrowUpDown, Pencil, Plus, Search, Trash2} from 'lucide-react';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import {RearrangeCategoriesView} from 'src/CategoriesView/RearrangeCategoriesView';
import {
  categorySwatchClasses,
  validCategoryColorOrNull,
} from 'src/categoryAppearance';
import {CategoryAvatar} from 'src/CategoryAvatar';
import ActionHeader from 'src/components/ActionHeader';
import {cn} from 'src/lib/utils';
import {useModals} from 'src/Modals';
import {PopoverMenu} from 'src/PopoverMenu';
import PopoverMenuItem from 'src/PopoverMenuItem';
import type {FullCategoryFragment} from '@transactions/graphql';

type CategoryRowProps = {
  category: FullCategoryFragment;
  onEdit: () => void;
  onDelete: () => void;
};

const CategoryRow = ({category, onEdit, onDelete}: CategoryRowProps) => (
  <div className="border-border bg-card hover:bg-accent flex items-center justify-between rounded-lg border pr-2 transition-colors">
    <Link
      to={`/categories/${category._id}`}
      className="flex min-w-0 flex-1 items-center gap-3 p-4 no-underline"
    >
      <CategoryAvatar category={category} />
      <span className="text-foreground truncate font-medium">
        {category.name}
      </span>
    </Link>
    <PopoverMenu>
      <PopoverMenuItem iconLeft={<Pencil />} onSelect={onEdit}>
        Edit
      </PopoverMenuItem>
      <PopoverMenuItem
        iconLeft={<Trash2 className="text-destructive" />}
        className="text-destructive"
        onSelect={onDelete}
      >
        Delete
      </PopoverMenuItem>
    </PopoverMenu>
  </div>
);

export const CategoriesView = () => {
  useBreadcrumb({label: 'Categories', reset: true});
  const {data, loading, refetch} = useAllCategoriesQuery();
  const {data: groupsData} = useAllCategoryGroupsQuery();
  const {createCategoryModal, editCategoryModal, deleteCategoryModal} =
    useModals();
  const [search, setSearch] = React.useState('');
  const [rearranging, setRearranging] = React.useState(false);

  const handleRefetch = () => {
    refetch();
  };

  const handleCreate = () => {
    createCategoryModal.openModal({onSuccess: handleRefetch});
  };

  const renderRow = (category: FullCategoryFragment) => (
    <CategoryRow
      key={category._id}
      category={category}
      onEdit={() =>
        editCategoryModal.openModal({
          category,
          onUpdate: handleRefetch,
          onDelete: handleRefetch,
        })
      }
      onDelete={() =>
        deleteCategoryModal.openModal({category, onDeleted: handleRefetch})
      }
    />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">Loading categories...</span>
      </div>
    );
  }

  const allCategories = data?.allCategories || [];
  const groups = groupsData?.allCategoryGroups || [];

  const categoryById = new Map(allCategories.map((c) => [c._id, c]));
  const groupedIds = new Set(groups.flatMap((g) => g.categoryIds));
  const matchesSearch = (category: FullCategoryFragment) =>
    category.name.toLowerCase().includes(search.toLowerCase());

  // Grouped sections in group order (members in member order), then the
  // ungrouped remainder sorted by name (allCategories arrives name-sorted).
  const sections = groups.map((group) => ({
    key: group._id,
    label: group.name,
    color: validCategoryColorOrNull(group.color ?? null),
    categories: group.categoryIds
      .flatMap((id) => {
        const category = categoryById.get(id);
        return category ? [category] : [];
      })
      .filter(matchesSearch),
  }));
  const ungrouped = allCategories
    .filter((c) => !groupedIds.has(c._id))
    .filter(matchesSearch);
  const visibleCount =
    sections.reduce((sum, s) => sum + s.categories.length, 0) +
    ungrouped.length;

  const actions = (
    <>
      <Button
        variant={rearranging ? 'outline' : 'ghost'}
        size={rearranging ? undefined : 'icon'}
        onClick={() => setRearranging((v) => !v)}
        title={rearranging ? 'Done rearranging' : 'Rearrange groups'}
      >
        {rearranging ? 'Done' : <ArrowUpDown className="size-5" />}
      </Button>
      <Button variant="ghost" size="icon" onClick={handleCreate}>
        <Plus className="size-5" />
      </Button>
    </>
  );

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <ActionHeader
        content={`${allCategories.length} categories`}
        actions={actions}
      />
      <div className="flex flex-col gap-4 p-4">
        {rearranging ? (
          <RearrangeCategoriesView
            categories={allCategories}
            groups={groups}
            groupsLoaded={groupsData != null}
          />
        ) : (
          <>
            <div className="relative">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search categories..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border-border bg-background placeholder:text-muted-foreground w-full rounded-md border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {allCategories.length === 0 ? (
              <div className="border-border rounded-lg border border-dashed p-8 text-center">
                <p className="text-muted-foreground mb-4">
                  No categories created yet
                </p>
                <p className="text-muted-foreground mb-4 text-sm">
                  Categories help you organize your transactions by type, like
                  "Groceries", "Entertainment", or "Utilities".
                </p>
                <Button variant="outline" onClick={handleCreate} type="button">
                  Create your first category
                </Button>
              </div>
            ) : visibleCount === 0 ? (
              <div className="text-muted-foreground py-8 text-center text-sm">
                No categories matching "{search}"
              </div>
            ) : groups.length === 0 ? (
              <div className="flex flex-col gap-2">{ungrouped.map(renderRow)}</div>
            ) : (
              <div className="flex flex-col gap-6">
                {sections
                  .filter((section) => section.categories.length > 0)
                  .map((section) => (
                    <div key={section.key}>
                      <div className="mb-2 flex items-baseline gap-2">
                        {section.color != null && (
                          <span
                            className={cn(
                              'size-2 shrink-0 self-center rounded-full',
                              categorySwatchClasses[section.color],
                            )}
                          />
                        )}
                        <h3 className="text-foreground text-sm font-semibold">
                          {section.label}
                        </h3>
                        <span className="text-muted-foreground text-xs">
                          {section.categories.length}
                        </span>
                      </div>
                      <div className="flex flex-col gap-2">
                        {section.categories.map(renderRow)}
                      </div>
                    </div>
                  ))}
                {ungrouped.length > 0 && (
                  <div>
                    <div className="mb-2 flex items-baseline gap-2">
                      <h3 className="text-foreground text-sm font-semibold">
                        Ungrouped
                      </h3>
                      <span className="text-muted-foreground text-xs">
                        {ungrouped.length}
                      </span>
                    </div>
                    <div className="flex flex-col gap-2">
                      {ungrouped.map(renderRow)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
